import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Asset {
  id: string
  asset_tag: string
  name: string
  category_name: string
  location_name: string
  status: string
  purchase_cost: number
  book_value: number
  assigned_to: string | null
  purchase_date: string
}

export default function AssetManagementPage() {
  const [assets, setAssets] = useState<Asset[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [assetsRes, catsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/assets/'),
      fetch(API_BASE + '/assets/categories'),
      fetch(API_BASE + '/assets/statistics')
    ])
    if (assetsRes.ok) setAssets((await assetsRes.json()).assets || [])
    if (catsRes.ok) setCategories((await catsRes.json()).categories || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'in_maintenance': return 'bg-yellow-100 text-yellow-800'
      case 'retired': return 'bg-gray-100 text-gray-600'
      case 'disposed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Asset Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Asset</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Assets</div>
              <div className="text-2xl font-bold">{statistics.total_assets}</div>
              <div className="text-xs text-green-600">{statistics.active_assets} active</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Purchase Value</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.total_purchase_value)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Book Value</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.current_book_value)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Depreciation</div>
              <div className="text-2xl font-bold text-orange-600">{formatCurrency(statistics.total_depreciation)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-6 gap-4 mb-8">
          {categories.map(cat => (
            <div key={cat.id} className="bg-white rounded-lg shadow p-4">
              <div className="font-semibold text-sm mb-1">{cat.name}</div>
              <div className="text-2xl font-bold text-blue-600">{cat.asset_count}</div>
              <div className="text-xs text-gray-500">{cat.useful_life_years}yr life</div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Asset Inventory</h2></div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {assets.slice(0, 12).map(asset => (
                <tr key={asset.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium">{asset.name}</div>
                    <div className="text-xs text-gray-500">{asset.asset_tag}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm">{asset.category_name}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">{asset.location_name}</div>
                    {asset.assigned_to && <div className="text-xs text-gray-500">→ {asset.assigned_to}</div>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium">{formatCurrency(asset.book_value)}</div>
                    <div className="text-xs text-gray-500">Cost: {formatCurrency(asset.purchase_cost)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(asset.status)}`}>
                      {asset.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}
